/**
 * Gestión de usuarios del portal de clientes desde panel-admin.
 *
 * Layout: agrupado por empresa del CRM. Cada empresa cliente aparece
 * como una sección con sus miembros adentro + botón "Invitar miembro"
 * que pre-fila el companyId. Empresas con 0 miembros pero status
 * `active` aparecen vacías para que el admin pueda dar de alta sin
 * navegar a otro lado.
 *
 * Flujo invite:
 *   1. Click "Invitar miembro" en una sección de empresa.
 *   2. Form pre-fila companyId; admin escribe email + checkboxes de permisos.
 *   3. Backend devuelve {inviteUrl}; banner amarillo lo muestra para copiar.
 *   4. Admin comparte la URL al cliente (WhatsApp/llamada).
 *
 * Reset y desactivar también disponibles por user.
 *
 * Permisos editables inline: botón "Accesos" → modal con checkboxes
 * de los permisos del user; PATCH a /admin/clients/:id.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Copy,
  ExternalLink,
  KeyRound,
  Network,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api, ApiError } from "@/lib/api";

export const Route = createFileRoute("/admin/clients")({
  component: AdminClientsPage,
});

interface CompanyChip {
  id: string;
  name: string;
  roleInCompany: string;
}

interface ClientUserRow {
  id: string;
  email: string;
  name: string;
  role: "admin" | "member";
  permissions: string[];
  active: boolean;
  lastLoginAt: number | null;
  createdAt: number;
  companies: CompanyChip[];
}

const PERMISSIONS = [
  { key: "tickets.view", label: "Ver tablero de tickets" },
  { key: "tickets.create", label: "Crear tickets nuevos" },
  { key: "tickets.comment", label: "Comentar en tickets" },
  { key: "invoices.view", label: "Ver facturas" },
  { key: "archives.view", label: "Ver archivos" },
  { key: "archives.upload", label: "Subir archivos" },
  { key: "uptime.view", label: "Ver estado de servidores" },
] as const;
type PermissionKey = (typeof PERMISSIONS)[number]["key"];
const DEFAULT_PERMISSIONS: PermissionKey[] = [
  "tickets.view",
  "tickets.create",
  "tickets.comment",
];
const PERMISSION_LABEL = new Map<string, string>(PERMISSIONS.map((p) => [p.key, p.label]));

interface PendingInvite {
  id: string;
  email: string;
  role: "admin" | "member";
  companyId: string | null;
  companyName: string | null;
  expiresAt: number;
  createdAt: number;
}

interface CompanyRow {
  id: string;
  name: string;
  status: "active" | "prospect" | "past" | "lost";
  isInternal: boolean;
  isSupplier: boolean;
  industry: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  active: "Activo",
  prospect: "Prospecto",
  past: "Pasado",
  lost: "Perdido",
};
const STATUS_BADGE: Record<string, string> = {
  active: "badge-success",
  prospect: "badge-info",
  past: "badge-ghost",
  lost: "badge-error",
};

function AdminClientsPage() {
  const qc = useQueryClient();
  const usersQ = useQuery<{ clientUsers: ClientUserRow[] }>({
    queryKey: ["admin", "clients"],
    queryFn: () => api.get("/admin/clients"),
  });
  const invitesQ = useQuery<{ invites: PendingInvite[] }>({
    queryKey: ["admin", "clients", "invites", "pending"],
    queryFn: () => api.get("/admin/clients/invites/pending"),
  });
  const companiesQ = useQuery<{ companies: CompanyRow[] }>({
    queryKey: ["companies"],
    queryFn: () => api.get("/companies"),
  });

  const [inviteCompany, setInviteCompany] = useState<CompanyRow | null>(null);
  const [editAccess, setEditAccess] = useState<ClientUserRow | null>(null);
  const [orgCompany, setOrgCompany] = useState<CompanyRow | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<{ url: string; kind: string } | null>(null);

  const deactivate = useMutation({
    mutationFn: (id: string) => api.del(`/admin/clients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "clients"] }),
  });
  const resetPassword = useMutation<
    { resetUrl: string; expiresAt: number },
    Error,
    string
  >({
    mutationFn: (id) => api.post(`/admin/clients/${id}/reset-password`, {}),
    onSuccess: (res) => setGeneratedUrl({ url: res.resetUrl, kind: "reset" }),
  });
  const regenInvite = useMutation<
    { inviteUrl: string; expiresAt: number },
    Error,
    string
  >({
    mutationFn: (id) => api.post(`/admin/clients/invites/${id}/regenerate`, {}),
    onSuccess: (res) => {
      setGeneratedUrl({ url: res.inviteUrl, kind: "invite" });
      qc.invalidateQueries({ queryKey: ["admin", "clients", "invites", "pending"] });
    },
  });

  // Agrupar users por company. Un user puede pertenecer a varias
  // companies (agencia), así que aparece en cada sección. Para users
  // sin company asignada, usamos la sección "sin empresa".
  const { byCompany, unassigned, companiesWithoutUsers } = useMemo(() => {
    const users = usersQ.data?.clientUsers ?? [];
    const companies = (companiesQ.data?.companies ?? []).filter(
      (c) => !c.isInternal && (c.status === "active" || c.status === "prospect"),
    );
    const companiesById = new Map(companies.map((c) => [c.id, c]));
    const byCompany = new Map<string, { company: CompanyRow; users: ClientUserRow[] }>();
    const unassigned: ClientUserRow[] = [];
    for (const u of users) {
      if (u.companies.length === 0) {
        unassigned.push(u);
        continue;
      }
      for (const c of u.companies) {
        const company = companiesById.get(c.id) ?? null;
        if (!company) continue; // empresa archivada/borrada — skip
        const entry = byCompany.get(c.id) ?? { company, users: [] };
        entry.users.push(u);
        byCompany.set(c.id, entry);
      }
    }
    const companiesWithoutUsers = companies.filter((c) => !byCompany.has(c.id));
    return { byCompany, unassigned, companiesWithoutUsers };
  }, [usersQ.data, companiesQ.data]);

  const totalUsers = usersQ.data?.clientUsers?.length ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="admin"
        title="Clientes externos"
        subtitle="Usuarios del portal en clientes.axorial.mx, agrupados por empresa del CRM. Los invites se generan como URL y se comparten manualmente — NO se mandan emails."
        actions={
          <Link to="/admin" className="btn btn-ghost btn-sm">
            <ArrowLeft className="size-4" /> Admin
          </Link>
        }
      />

      {generatedUrl && (
        <UrlBanner
          url={generatedUrl.url}
          kind={generatedUrl.kind}
          onDismiss={() => setGeneratedUrl(null)}
        />
      )}

      {invitesQ.data && invitesQ.data.invites.length > 0 && (
        <section className="card-elevated p-4 space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-primary">
            Invites pendientes · {invitesQ.data.invites.length}
          </h3>
          <ul className="divide-y divide-base-300/40 -mx-1">
            {invitesQ.data.invites.map((inv) => (
              <li key={inv.id} className="px-1 py-2 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{inv.email}</div>
                  <div className="text-xs text-base-content/55 truncate">
                    {inv.companyName ?? "(sin empresa)"} · expira{" "}
                    {new Date(inv.expiresAt * 1000).toLocaleString("es-MX", {
                      day: "numeric",
                      month: "short",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => regenInvite.mutate(inv.id)}
                  disabled={regenInvite.isPending}
                  className="btn btn-ghost btn-xs gap-1"
                  title="Generar nueva URL (revoca la anterior)"
                >
                  <RefreshCw className="size-3" /> Regenerar
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {usersQ.isLoading || companiesQ.isLoading ? (
        <div className="grid place-items-center py-16">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Empresas con miembros activos */}
          {[...byCompany.values()]
            .sort((a, b) => a.company.name.localeCompare(b.company.name))
            .map(({ company, users }) => (
              <CompanySection
                key={company.id}
                company={company}
                users={users}
                onInvite={() => setInviteCompany(company)}
                onOrg={() => setOrgCompany(company)}
                onResetPassword={(id) => resetPassword.mutate(id)}
                onDeactivate={(u) => {
                  if (confirm(`¿Desactivar a ${u.name}?`)) deactivate.mutate(u.id);
                }}
                onEditAccess={(u) => setEditAccess(u)}
                pending={resetPassword.isPending || deactivate.isPending}
              />
            ))}

          {/* Empresas sin miembros aún (active/prospect) — atajo para invitar */}
          {companiesWithoutUsers.length > 0 && (
            <section className="card-elevated p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-primary mb-3">
                Empresas sin acceso al portal · {companiesWithoutUsers.length}
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {companiesWithoutUsers.map((c) => (
                  <li
                    key={c.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-md border border-base-300/60 bg-base-100"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <span
                        className={`badge badge-xs ${STATUS_BADGE[c.status] ?? "badge-ghost"}`}
                      >
                        {STATUS_LABEL[c.status]}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setInviteCompany(c)}
                      className="btn btn-ghost btn-xs gap-1"
                      title="Invitar miembros de esta empresa"
                    >
                      <UserPlus className="size-3" /> Invitar
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Users sin empresa asignada (edge case) */}
          {unassigned.length > 0 && (
            <section className="card-elevated p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-warning mb-3">
                Usuarios sin empresa asignada · {unassigned.length}
              </h3>
              <ul className="divide-y divide-base-300/40 -mx-1">
                {unassigned.map((u) => (
                  <ClientUserItem
                    key={u.id}
                    user={u}
                    onResetPassword={(id) => resetPassword.mutate(id)}
                    onDeactivate={() => {
                      if (confirm(`¿Desactivar a ${u.name}?`)) deactivate.mutate(u.id);
                    }}
                    onEditAccess={() => setEditAccess(u)}
                    pending={resetPassword.isPending || deactivate.isPending}
                  />
                ))}
              </ul>
            </section>
          )}

          {totalUsers === 0 && companiesWithoutUsers.length === 0 && (
            <div className="card-elevated p-8 text-center text-sm text-base-content/55">
              No hay empresas-cliente activas en el CRM. Crea una primero
              desde la sección CRM.
            </div>
          )}
        </div>
      )}

      {inviteCompany && (
        <InviteDialog
          company={inviteCompany}
          onClose={() => setInviteCompany(null)}
          onCreated={(url) => {
            setGeneratedUrl({ url, kind: "invite" });
            setInviteCompany(null);
            qc.invalidateQueries({ queryKey: ["admin", "clients", "invites", "pending"] });
          }}
        />
      )}
      {editAccess && (
        <EditAccessDialog
          user={editAccess}
          onClose={() => setEditAccess(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "clients"] });
            setEditAccess(null);
          }}
        />
      )}
      {orgCompany && (
        <OrgChartDialog company={orgCompany} onClose={() => setOrgCompany(null)} />
      )}
    </div>
  );
}

function CompanySection({
  company,
  users,
  onInvite,
  onOrg,
  onResetPassword,
  onDeactivate,
  onEditAccess,
  pending,
}: {
  company: CompanyRow;
  users: ClientUserRow[];
  onInvite: () => void;
  onOrg: () => void;
  onResetPassword: (id: string) => void;
  onDeactivate: (u: ClientUserRow) => void;
  onEditAccess: (u: ClientUserRow) => void;
  pending: boolean;
}) {
  return (
    <section className="card-elevated overflow-hidden">
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap border-b border-base-300/40">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="size-9 rounded-lg bg-primary/15 text-primary grid place-items-center">
            <Users className="size-4" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-semibold truncate flex items-center gap-2">
              {company.name}
              <span className={`badge badge-xs ${STATUS_BADGE[company.status] ?? "badge-ghost"}`}>
                {STATUS_LABEL[company.status]}
              </span>
            </div>
            <div className="text-xs text-base-content/55">
              {users.length} {users.length === 1 ? "miembro" : "miembros"}
              {company.industry ? ` · ${company.industry}` : ""}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onOrg}
          className="btn btn-ghost btn-sm gap-1 shrink-0"
          title="Organigrama interno (no se expone al cliente)"
        >
          <Network className="size-3.5" /> Organigrama
        </button>
        <button
          type="button"
          onClick={onInvite}
          className="btn btn-primary btn-sm gap-1 shrink-0"
        >
          <UserPlus className="size-3.5" /> Invitar miembro
        </button>
      </div>
      <ul className="divide-y divide-base-300/40">
        {users.map((u) => (
          <ClientUserItem
            key={u.id}
            user={u}
            onResetPassword={(id) => onResetPassword(id)}
            onDeactivate={() => onDeactivate(u)}
            onEditAccess={() => onEditAccess(u)}
            pending={pending}
          />
        ))}
      </ul>
    </section>
  );
}

function ClientUserItem({
  user: u,
  onResetPassword,
  onDeactivate,
  onEditAccess,
  pending,
}: {
  user: ClientUserRow;
  onResetPassword: (id: string) => void;
  onDeactivate: () => void;
  onEditAccess: () => void;
  pending: boolean;
}) {
  return (
    <li
      className={
        "p-4 flex items-center gap-3 flex-wrap " + (u.active ? "" : "opacity-55")
      }
    >
      <div className="size-9 rounded-full bg-primary/15 text-primary grid place-items-center font-semibold shrink-0">
        {u.name
          .split(" ")
          .slice(0, 2)
          .map((w) => w[0]?.toUpperCase() ?? "")
          .join("")}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate flex items-center gap-2">
          {u.name}
          {!u.active && <span className="badge badge-ghost badge-xs">Desactivado</span>}
          {u.role === "admin" && <span className="badge badge-primary badge-xs">Admin</span>}
        </div>
        <div className="text-xs text-base-content/55 truncate">{u.email}</div>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {u.permissions.length === 0 ? (
            <span className="text-[10px] text-base-content/45 italic">
              Sin permisos extra (solo ve sus proyectos)
            </span>
          ) : (
            u.permissions.map((p) => (
              <span key={p} className="badge badge-xs badge-outline">
                {PERMISSION_LABEL.get(p) ?? p}
              </span>
            ))
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onEditAccess}
          className="btn btn-ghost btn-xs gap-1"
          title="Editar permisos del portal"
        >
          <Settings2 className="size-3" /> Accesos
        </button>
        <button
          type="button"
          onClick={() => onResetPassword(u.id)}
          disabled={pending}
          className="btn btn-ghost btn-xs gap-1"
          title="Generar URL de reset de password"
        >
          <KeyRound className="size-3" /> Reset
        </button>
        {u.active && (
          <button
            type="button"
            onClick={onDeactivate}
            disabled={pending}
            className="btn btn-ghost btn-xs gap-1 text-error"
            title="Desactivar (soft delete)"
          >
            <Trash2 className="size-3" />
          </button>
        )}
      </div>
    </li>
  );
}

/**
 * Banner persistente que muestra la URL generada (invite o reset) con
 * botón para copiar. Esto es el único momento en que el admin la ve
 * — al cerrar el banner el token raw se pierde (la BD sólo guarda el
 * hash SHA-256).
 */
function UrlBanner({
  url,
  kind,
  onDismiss,
}: {
  url: string;
  kind: string;
  onDismiss: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      inputRef.current?.select();
    }
  };
  return (
    <div className="card-elevated p-4 border border-warning/40 bg-warning/5">
      <div className="flex items-start gap-3 mb-2">
        <div className="size-8 rounded-lg bg-warning/20 text-warning grid place-items-center shrink-0">
          <ExternalLink className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">
            URL {kind === "invite" ? "de invitación" : "de reset de password"} generada
          </div>
          <div className="text-xs text-base-content/65 leading-relaxed mt-0.5">
            Copia y compártela manualmente con el cliente (WhatsApp / llamada).
            Esta URL no se vuelve a mostrar — al cerrar este banner se pierde.
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="btn btn-ghost btn-xs btn-square"
          aria-label="Cerrar"
        >
          <X className="size-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          readOnly
          value={url}
          className="input input-bordered input-sm flex-1 font-mono text-xs"
          onFocus={(e) => e.currentTarget.select()}
        />
        <button type="button" onClick={copy} className="btn btn-warning btn-sm gap-1">
          <Copy className="size-3.5" /> {copied ? "Copiado!" : "Copiar"}
        </button>
      </div>
    </div>
  );
}

function InviteDialog({
  company,
  onClose,
  onCreated,
}: {
  company: CompanyRow;
  onClose: () => void;
  onCreated: (url: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [permissions, setPermissions] = useState<Set<PermissionKey>>(
    () => new Set(DEFAULT_PERMISSIONS),
  );
  const [error, setError] = useState<string | null>(null);

  const togglePerm = (k: PermissionKey) => {
    setPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const create = useMutation<{ inviteUrl: string }, Error, void>({
    mutationFn: () =>
      api.post("/admin/clients/invites", {
        email: email.trim().toLowerCase(),
        companyId: company.id,
        permissions: [...permissions],
      }),
    onSuccess: (res) => onCreated(res.inviteUrl),
    onError: (e) => {
      const detail = e instanceof ApiError ? JSON.stringify(e.payload) : (e.message ?? "Error");
      setError(detail);
    },
  });

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-md">
        <div className="flex items-baseline justify-between mb-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-base-content/55">
              Invitar a {company.name}
            </div>
            <h3 className="font-display font-semibold text-lg">Nuevo miembro</h3>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-square">
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-3">
          <label className="form-control w-full">
            <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
              Email del cliente
            </span>
            <input
              type="email"
              className="input input-bordered w-full"
              placeholder="contacto@empresa.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          </label>
          <div className="form-control w-full">
            <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
              Permisos
            </span>
            <div className="text-xs text-base-content/55 mb-2">
              Ver sus proyectos siempre está habilitado. Marca las pestañas
              y acciones adicionales a las que tendrá acceso.
            </div>
            <div className="rounded-md border border-base-300/60 divide-y divide-base-300/40">
              {PERMISSIONS.map((p) => (
                <label
                  key={p.key}
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-base-200/40"
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-primary"
                    checked={permissions.has(p.key)}
                    onChange={() => togglePerm(p.key)}
                  />
                  <span className="text-sm">{p.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-3 text-xs text-error border border-error/30 bg-error/5 rounded-md p-2">
            {error}
          </div>
        )}

        <div className="modal-action">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => create.mutate()}
            disabled={create.isPending || !email.trim().includes("@")}
            className="btn btn-primary btn-sm"
          >
            {create.isPending && <span className="loading loading-spinner loading-xs" />}
            <Plus className="size-4" /> Generar URL
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Edit inline de permisos del client_user. Modal con checkboxes;
 * PATCH /admin/clients/:id con {permissions: [...]}.
 */
function EditAccessDialog({
  user,
  onClose,
  onSaved,
}: {
  user: ClientUserRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [permissions, setPermissions] = useState<Set<PermissionKey>>(
    () => new Set(user.permissions as PermissionKey[]),
  );
  const [error, setError] = useState<string | null>(null);
  const togglePerm = (k: PermissionKey) => {
    setPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/admin/clients/${user.id}`, { permissions: [...permissions] }),
    onSuccess: onSaved,
    onError: (e) =>
      setError(e instanceof ApiError ? JSON.stringify(e.payload) : (e as Error).message),
  });
  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-md">
        <div className="flex items-baseline justify-between mb-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-base-content/55">
              Accesos de
            </div>
            <h3 className="font-display font-semibold text-lg truncate">{user.name}</h3>
            <div className="text-xs text-base-content/55 truncate">{user.email}</div>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-square">
            <X className="size-4" />
          </button>
        </div>
        <div className="text-xs text-base-content/55 mb-3">
          Marca las pestañas y acciones a las que tiene acceso. Ver proyectos
          es implícito y no se puede revocar (para revocar acceso completo,
          desactiva al usuario).
        </div>
        <div className="rounded-md border border-base-300/60 divide-y divide-base-300/40">
          {PERMISSIONS.map((p) => (
            <label
              key={p.key}
              className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-base-200/40"
            >
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-primary"
                checked={permissions.has(p.key)}
                onChange={() => togglePerm(p.key)}
              />
              <span className="text-sm">{p.label}</span>
            </label>
          ))}
        </div>
        {error && (
          <div className="mt-3 text-xs text-error border border-error/30 bg-error/5 rounded-md p-2">
            {error}
          </div>
        )}
        <div className="modal-action">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="btn btn-primary btn-sm"
          >
            {save.isPending && <span className="loading loading-spinner loading-xs" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

interface OrgMember {
  id: string;
  companyId: string;
  parentId: string | null;
  name: string;
  position: string | null;
  email: string | null;
  displayOrder: number;
}

/**
 * Editor del organigrama INTERNO de un cliente. NUNCA expuesto al
 * portal — sólo lo ve y edita staff Axorial.
 */
function OrgChartDialog({
  company,
  onClose,
}: {
  company: CompanyRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ members: OrgMember[] }>({
    queryKey: ["admin", "client-org", company.id],
    queryFn: () => api.get(`/admin/companies/${company.id}/org`),
  });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<OrgMember | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/admin/org/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "client-org", company.id] }),
  });

  const members = data?.members ?? [];

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-2xl relative">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-base-content/55">
              Organigrama interno · {company.name}
            </div>
            <h3 className="font-display font-semibold text-lg">Contactos del cliente</h3>
            <div className="text-xs text-base-content/55 mt-1">
              Sólo lo vemos nosotros. El cliente nunca lo ve en su portal.
            </div>
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm btn-square">
            <X className="size-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-8">
            <span className="loading loading-spinner text-primary" />
          </div>
        ) : members.length === 0 ? (
          <div className="text-center text-sm text-base-content/55 py-6">
            Sin contactos registrados todavía.
          </div>
        ) : (
          <ul className="divide-y divide-base-300/40 -mx-1 max-h-[50vh] overflow-y-auto">
            {members.map((m) => {
              const parent = members.find((x) => x.id === m.parentId);
              return (
                <li key={m.id} className="px-1 py-2.5 flex items-center gap-3">
                  <div className="size-8 rounded-full bg-primary/15 text-primary grid place-items-center text-xs font-semibold">
                    {m.name
                      .split(" ")
                      .slice(0, 2)
                      .map((w) => w[0]?.toUpperCase() ?? "")
                      .join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">
                      {m.name}
                      {m.position && (
                        <span className="text-base-content/55 font-normal"> · {m.position}</span>
                      )}
                    </div>
                    {m.email && (
                      <div className="text-xs text-base-content/55 truncate">{m.email}</div>
                    )}
                    {parent && (
                      <div className="text-[10px] text-base-content/45 mt-0.5">
                        Reporta a: {parent.name}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditing(m)}
                    className="btn btn-ghost btn-xs"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`¿Borrar a ${m.name}?`)) remove.mutate(m.id);
                    }}
                    className="btn btn-ghost btn-xs text-error"
                  >
                    <Trash2 className="size-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="modal-action">
          <button type="button" onClick={() => setAdding(true)} className="btn btn-primary btn-sm">
            <Plus className="size-4" /> Agregar contacto
          </button>
        </div>

        {(adding || editing) && (
          <OrgMemberForm
            companyId={company.id}
            member={editing}
            allMembers={members}
            onClose={() => {
              setAdding(false);
              setEditing(null);
            }}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ["admin", "client-org", company.id] });
              setAdding(false);
              setEditing(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function OrgMemberForm({
  companyId,
  member,
  allMembers,
  onClose,
  onSaved,
}: {
  companyId: string;
  member: OrgMember | null;
  allMembers: OrgMember[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(member?.name ?? "");
  const [position, setPosition] = useState(member?.position ?? "");
  const [email, setEmail] = useState(member?.email ?? "");
  const [parentId, setParentId] = useState(member?.parentId ?? "");
  const [displayOrder, setDisplayOrder] = useState(member?.displayOrder ?? 0);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        position: position.trim() || null,
        email: email.trim() || null,
        parentId: parentId || null,
        displayOrder,
      };
      return member
        ? api.patch(`/admin/org/${member.id}`, body)
        : api.post(`/admin/companies/${companyId}/org`, body);
    },
    onSuccess: onSaved,
  });

  const candidates = allMembers.filter((m) => m.id !== member?.id);

  return (
    <div className="absolute inset-0 z-10 bg-base-100/95 p-5 overflow-y-auto rounded-md">
      <h4 className="font-display font-semibold mb-3">
        {member ? "Editar contacto" : "Nuevo contacto"}
      </h4>
      <div className="space-y-3">
        <label className="form-control">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Nombre
          </span>
          <input
            className="input input-bordered w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </label>
        <label className="form-control">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Puesto
          </span>
          <input
            className="input input-bordered w-full"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder="CEO, CTO, PM, etc."
          />
        </label>
        <label className="form-control">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Email (opcional)
          </span>
          <input
            type="email"
            className="input input-bordered w-full"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="form-control">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Reporta a
          </span>
          <select
            className="select select-bordered w-full"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">(sin jefe / top level)</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.position ? ` — ${c.position}` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="form-control">
          <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
            Orden de display
          </span>
          <input
            type="number"
            className="input input-bordered w-full"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
          />
        </label>
      </div>
      <div className="modal-action">
        <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending || !name.trim()}
          className="btn btn-primary btn-sm"
        >
          {save.isPending && <span className="loading loading-spinner loading-xs" />}
          Guardar
        </button>
      </div>
    </div>
  );
}
